/**
 * Collection pages: push sold-out products to the end *across pagination*.
 *
 * Shopify paginates collection products before Liquid renders them. That means doing
 * "in stock first, sold out last" in Liquid only reorders within the current page.
 *
 * This script builds a stable global order by:
 * - fetching each paginated section page (section rendering API)
 * - collecting all in-stock cards first (in original order)
 * - then collecting all sold-out cards (in original order)
 * - rendering the slice for the current `?page=`
 */
(function () {
  'use strict';

  const SOLD_OUT_BADGE_SELECTOR = '.badge--bottom-left.color-grey';
  const MAX_PAGES_TO_REORDER = 30; // safety guard for very large collections

  let suppressObserver = false;
  let running = false;
  let queued = false;
  let debounceTimer = null;
  let lastAppliedKey = '';
  const requestedOkendoRatings = new WeakSet();

  function isMeiliSearchPage() {
    return !!document.querySelector('.section-meili-search');
  }

  function getProductGridEl() {
    return document.getElementById('product-grid');
  }

  function getProductGridContainerEl() {
    return document.getElementById('ProductGridContainer');
  }

  function getSectionInstanceId() {
    const grid = getProductGridEl();
    return (grid && grid.dataset && grid.dataset.id) ? String(grid.dataset.id) : '';
  }

  function getPerPage() {
    const grid = getProductGridEl();
    if (!grid) return 0;

    const rawSettings = grid.getAttribute('data-settings') || '';
    try {
      const settings = JSON.parse(rawSettings);
      const n = parseInt(settings?.products_per_page, 10);
      if (Number.isFinite(n) && n > 0) return n;
    } catch (e) {
      // ignore
    }

    const count = grid.querySelectorAll(':scope > li').length;
    return count || 0;
  }

  function getCurrentPage() {
    const params = new URLSearchParams(window.location.search || '');
    const n = parseInt(params.get('page') || '1', 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  function getTotalPagesFromDom() {
    const els = Array.from(document.querySelectorAll('.pagination-wrapper .pagination__item'));
    const nums = els
      .map((el) => parseInt((el.textContent || '').trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    return nums.length ? Math.max(...nums) : 1;
  }

  function hasSoldOutItemsOnPage() {
    const grid = getProductGridEl();
    if (!grid) return false;
    return !!grid.querySelector(SOLD_OUT_BADGE_SELECTOR);
  }

  function buildSectionFetchUrl(pageNumber) {
    const sectionId = getSectionInstanceId();
    if (!sectionId) return null;

    const base = new URL(window.location.href);
    const params = new URLSearchParams(base.search);

    // Keep existing filters/sort/etc., but control pagination + section rendering.
    params.delete('section_id');
    params.set('section_id', sectionId);
    params.set('page', String(pageNumber));

    base.search = params.toString();
    return base.toString();
  }

  function splitItemsByAvailabilityFromHtml(htmlText) {
    const doc = new DOMParser().parseFromString(htmlText, 'text/html');
    const grid = doc.getElementById('product-grid');
    if (!grid) return { inStock: [], soldOut: [] };

    const lis = Array.from(grid.children).filter((el) => el && el.tagName === 'LI');
    const inStock = [];
    const soldOut = [];

    lis.forEach((li) => {
      const isSoldOut = !!li.querySelector(SOLD_OUT_BADGE_SELECTOR);
      (isSoldOut ? soldOut : inStock).push(li.outerHTML);
    });

    return { inStock, soldOut };
  }

  async function fetchPageItems(pageNumber) {
    const url = buildSectionFetchUrl(pageNumber);
    if (!url) return { inStock: [], soldOut: [] };

    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Section fetch failed (page ${pageNumber}): HTTP ${res.status}`);
    const html = await res.text();
    return splitItemsByAvailabilityFromHtml(html);
  }

  async function applySoldOutLastAcrossPages() {
    // Only run on collection pages (and not the Meili /search page).
    if (isMeiliSearchPage()) return;

    const grid = getProductGridEl();
    const container = getProductGridContainerEl();
    if (!grid || !container) return;

    const totalPages = getTotalPagesFromDom();
    if (totalPages <= 1) return;

    // Guard against huge collections (too many section fetches).
    if (totalPages > MAX_PAGES_TO_REORDER) return;

    const currentPage = getCurrentPage();

    const perPage = getPerPage();
    if (!perPage) return;

    const sectionId = getSectionInstanceId();
    const runKey = `${sectionId}|${window.location.pathname}|${window.location.search}|${currentPage}|${perPage}`;
    if (runKey && runKey === lastAppliedKey) return;

    // Optimization: if we're on page 1 and there are no sold-out products in the first slice,
    // there's nothing to "pull up" from later pages.
    if (currentPage === 1 && !hasSoldOutItemsOnPage()) return;

    const allInStock = [];
    const allSoldOut = [];

    const start = (currentPage - 1) * perPage;
    const end = start + perPage;

    // Fetch pages in order and build a stable global ordering.
    // For non-last pages, we can stop once we have enough *in-stock* items to fill up to `end`.
    for (let p = 1; p <= totalPages; p++) {
      const { inStock, soldOut } = await fetchPageItems(p);
      allInStock.push(...inStock);
      allSoldOut.push(...soldOut);

      if (currentPage !== totalPages && allInStock.length >= end) {
        break;
      }
    }

    // If this page falls entirely within the in-stock region, we don't need sold-out HTML at all.
    // Otherwise (typically last pages), fetch all pages so we can render the full in-stock + sold-out list.
    let slice = [];
    if (allInStock.length >= end) {
      slice = allInStock.slice(start, end);
    } else {
      const sorted = allInStock.concat(allSoldOut);
      slice = sorted.slice(start, end);
    }

    // A filter/navigation may have replaced this grid while pages were fetched.
    if (grid !== getProductGridEl()) return;

    const template = document.createElement('template');
    template.innerHTML = slice.join('');
    const productId = (card) => card.querySelector('.card[data-product-id]')?.dataset.productId;
    const existingCards = new Map(Array.from(grid.children).map(card => [productId(card), card]));
    const nextCards = Array.from(template.content.children);
    const nextIds = new Set(nextCards.map(productId));

    // Never remove an active purchase from view during a background reorder.
    if (Array.from(grid.children).some(card =>
      card.querySelector('[data-eg-atc-state]') && !nextIds.has(productId(card)))) return;

    const focusedControl = grid.contains(document.activeElement) ? document.activeElement : null;
    const fragment = document.createDocumentFragment();
    nextCards.forEach(card => {
      const id = productId(card);
      // Reuse existing nodes to preserve pending requests, variants and listeners.
      fragment.append((id && existingCards.get(id)) || card);
    });

    suppressObserver = true;
    try {
      const prevOverflowAnchor = document.documentElement.style.overflowAnchor;
      document.documentElement.style.overflowAnchor = 'none';
      grid.replaceChildren(fragment);
      if (focusedControl?.isConnected) focusedControl.focus({ preventScroll: true });
      document.documentElement.style.overflowAnchor = prevOverflowAnchor;
      lastAppliedKey = runKey;
    } finally {
      // Let the DOM settle before re-enabling the observer.
      setTimeout(() => { suppressObserver = false; }, 0);
    }

    // Best-effort: initialize Okendo star ratings for the newly inserted cards.
    try {
      const reviewEls = grid.querySelectorAll('[data-oke-star-rating]:not([data-oke-rendered])');

      if (typeof window.okeWidgetApi?.initWidget === 'function') {
        reviewEls.forEach((el) => {
          if (!el.isConnected || requestedOkendoRatings.has(el)) return;
          requestedOkendoRatings.add(el);
          Promise.resolve().then(() => window.okeWidgetApi.initWidget(el)).catch((error) => {
            requestedOkendoRatings.delete(el);
            console.warn('[EG] Okendo star rating initialization failed:', error);
          });
        });
      } else if (typeof window.Okendo !== 'undefined' && typeof window.Okendo.initWidget === 'function') {
        window.Okendo.initWidget();
      }
    } catch (e) {
      // ignore
    }
  }

  async function run() {
    if (running) {
      queued = true;
      return;
    }
    running = true;
    try {
      await applySoldOutLastAcrossPages();
    } catch (e) {
      // Don’t break the page if this fails.
      console.warn('[EG] sold-out-last reorder failed:', e);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        run();
      }
    }
  }

  function schedule() {
    if (debounceTimer) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(run, 80);
  }

  // Initial run.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  // Re-run when facets update the collection via AJAX (they replace ProductGridContainer).
  const container = getProductGridContainerEl();
  if (container && 'MutationObserver' in window) {
    const obs = new MutationObserver(() => {
      if (suppressObserver) return;
      schedule();
    });
    // Shopify replaces `#ProductGridContainer` on filter/sort changes. Watching `subtree` causes
    // extra reruns when other scripts (ratings, variant pickers, etc.) tweak card markup.
    obs.observe(container, { childList: true, subtree: false });
  }

  // Back/forward navigation should also re-run.
  window.addEventListener('popstate', schedule);
})();

